import PageHeader from "../components/PageHeader";
import ConfigTabs from "../components/ConfigTabs";
import useSettings from "../hooks/useSettings";
import { Save } from "lucide-react";

export default function ConfigGeneral() {
  const { form, set, save, loading } = useSettings();

  if (!form) return <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading...</div>;

  return (
    <div data-testid="config-general-page">
      <PageHeader title="Configuration" subtitle="Site-wide configuration · General · identity, locale, and access" />
      <ConfigTabs />
      <form onSubmit={(e) => { e.preventDefault(); save(); }} className="bg-white border border-gray-200 rounded-sm p-6 max-w-4xl space-y-5" data-testid="config-general-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Site Name"><input value={form.site_name} onChange={(e) => set("site_name", e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="cfg-site-name" /></Field>
          <Field label="Tagline"><input value={form.site_tagline} onChange={(e) => set("site_tagline", e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" /></Field>

          <Field label="Language">
            <select value={form.language} onChange={(e) => set("language", e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="cfg-language">
              <option value="en">English</option>
              <option value="bn">Bangla</option>
              <option value="hi">Hindi</option>
            </select>
          </Field>
          <Field label="Timezone">
            <select value={form.timezone} onChange={(e) => set("timezone", e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="cfg-timezone">
              <option>Asia/Dhaka</option>
              <option>Asia/Kolkata</option>
              <option>Asia/Singapore</option>
              <option>UTC</option>
            </select>
          </Field>

          <Field label="Currency Code">
            <select value={form.currency} onChange={(e) => set("currency", e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="cfg-currency">
              <option>BDT</option>
              <option>USD</option>
              <option>INR</option>
              <option>EUR</option>
            </select>
          </Field>
          <Field label="Currency Symbol"><input value={form.currency_symbol} onChange={(e) => set("currency_symbol", e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" /></Field>

          <Field label="Date Format">
            <select value={form.date_format} onChange={(e) => set("date_format", e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm">
              <option>DD/MM/YYYY</option>
              <option>MM/DD/YYYY</option>
              <option>YYYY-MM-DD</option>
            </select>
          </Field>
          <Field label="Footer Note"><input value={form.footer_note} onChange={(e) => set("footer_note", e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" /></Field>
        </div>

        <div className="pt-4 border-t border-gray-100 space-y-3">
          <Toggle checked={form.maintenance_mode} onChange={(v) => set("maintenance_mode", v)} label="Maintenance Mode" desc="Temporarily hide the public site from players. Admin panel remains accessible." testId="cfg-maintenance" />
          {form.maintenance_mode && (
            <div className="ml-7 -mt-1 p-3 bg-amber-50 border border-amber-200 rounded-sm space-y-3" data-testid="cfg-maintenance-message-block">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-amber-800 font-semibold mb-1.5">Heading (big bold title)</label>
                <input
                  value={form.maintenance_title || ""}
                  onChange={(e) => set("maintenance_title", e.target.value)}
                  placeholder="আমরা শীঘ্রই ফিরে আসছি"
                  className="w-full h-10 px-3 border border-amber-300 rounded-sm text-sm bg-white focus:outline-none focus:border-amber-500"
                  data-testid="cfg-maintenance-title"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-amber-800 font-semibold mb-1.5">Message shown to players (Bengali / English)</label>
                <textarea
                  value={form.maintenance_message || ""}
                  onChange={(e) => set("maintenance_message", e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-amber-300 rounded-sm text-sm bg-white focus:outline-none focus:border-amber-500"
                  data-testid="cfg-maintenance-message"
                  placeholder="আমাদের সাইটে কিছু সমস্যা হয়েছে। দয়া করে আমাদের বিকল্প সাইটটি ব্যবহার করুন।"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-amber-800 font-semibold mb-1.5">Backup site URL (optional)</label>
                  <input
                    type="url"
                    value={form.maintenance_backup_url || ""}
                    onChange={(e) => set("maintenance_backup_url", e.target.value)}
                    placeholder="https://backup.maxlive88.com"
                    className="w-full h-10 px-3 border border-amber-300 rounded-sm text-sm bg-white font-mono tracking-tight focus:outline-none focus:border-amber-500"
                    data-testid="cfg-maintenance-backup-url"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-amber-800 font-semibold mb-1.5">Backup button label</label>
                  <input
                    value={form.maintenance_backup_label || ""}
                    onChange={(e) => set("maintenance_backup_label", e.target.value)}
                    placeholder="বিকল্প সাইট ব্যবহার করুন"
                    className="w-full h-10 px-3 border border-amber-300 rounded-sm text-sm bg-white focus:outline-none focus:border-amber-500"
                    data-testid="cfg-maintenance-backup-label"
                  />
                </div>
              </div>
              <p className="text-[11px] text-amber-700/90 leading-relaxed">
                Tip: backup URL খালি রাখলে শুধু message দেখাবে, button থাকবে না। URL দিলে users এক ক্লিকে আপনার বিকল্প সাইটে চলে যেতে পারবে।
              </p>
            </div>
          )}
          <Toggle checked={form.registration_enabled} onChange={(v) => set("registration_enabled", v)} label="Registration Enabled" desc="Allow new players to register on the public site." testId="cfg-registration" />
        </div>

        <div className="flex justify-end pt-4 border-t border-gray-100">
          <button type="submit" disabled={loading} className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="cfg-general-save">
            <Save size={14} /> {loading ? "Saving..." : "Save General Settings"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (<div><label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{label}</label>{children}</div>);
}
function Toggle({ checked, onChange, label, desc, testId }) {
  return (
    <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
      <div>
        <div className="text-sm font-semibold text-gray-900">{label}</div>
        <div className="text-xs text-gray-500">{desc}</div>
      </div>
    </label>
  );
}
